import { cn } from '@/lib/utils';
import { useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_SIZES = ['text-heading-18', 'text-heading-16', 'text-heading-14'] as const;

type HeadingSize = (typeof DEFAULT_SIZES)[number];

interface AutoSizeTextProps {
  children: React.ReactNode;
  sizes?: readonly HeadingSize[];
  className?: string;
}

export const AutoSizeText = ({ children, sizes = DEFAULT_SIZES, className }: AutoSizeTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [sizeClass, setSizeClass] = useState<HeadingSize>(sizes[0]!);
  const [ellipsis, setEllipsis] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const baseClasses = 'overflow-hidden text-primary whitespace-nowrap';

    for (const headingClass of sizes) {
      el.className = cn(baseClasses, headingClass, className);

      if (el.scrollWidth <= el.clientWidth) {
        setSizeClass(headingClass);
        setEllipsis(false);
        return;
      }
    }

    // Still overflows at smallest size — use ellipsis
    setSizeClass(sizes[sizes.length - 1]!);
    setEllipsis(true);
  }, [children, sizes, className]);

  return (
    <span
      ref={ref}
      className={cn(
        'overflow-hidden text-primary whitespace-nowrap',
        sizeClass,
        ellipsis && 'text-ellipsis break-all',
        className,
      )}
    >
      {children}
    </span>
  );
};
